import { addMonths } from 'date-fns';
import { ProductType } from '~/models/product/ProductType';
import type { ComparableSubscription } from '~/models/subscription-comparison/ComparableSubscriptions';
import type { NgtAvailabilityResponseYes, SanityBroadbandProduct } from './internet.api';
import { subscriptionFeatureMap } from './internet.constants';
import type { InternetProductWithBundleId, InternetServiceOrderProduct } from './useInternetStore';

export const mapSubsriptionsToInternetBundle = (
  subscriptions: ComparableSubscription[],
  ispProducts: InternetServiceOrderProduct[],
  sanityBroadbandProducts: SanityBroadbandProduct[],
) => {
  const subs = subscriptions.slice();
  subs.sort((a, b) => a.currentPrice - b.currentPrice);
  const data = subs
    .filter((s) => subscriptionFeatureMap.has(s.campaignUrl as any))
    .map((s) => {
      const ngtProduct = ispProducts.find((p) => p.productName.includes(s.campaignUrl === 'fleksipakken' ? '50' : '100')) ?? ispProducts[0];
      const price = ngtProduct.productPrice + s.currentPrice;
      const originalPrice = ngtProduct.originalProductPrice + s.originalPrice;
      return {
        subscription: s,
        ngtProduct,
        sanityProduct:
          sanityBroadbandProducts.find((p) =>
            s.campaignUrl === 'fleksipakken' ? p.productKey === 'ngt-fiber-50' : p.productKey === 'ngt-fiber-100',
          ) ?? sanityBroadbandProducts[0],
        // biome-ignore lint/style/noNonNullAssertion: check is done in filter
        features: subscriptionFeatureMap.get(s.campaignUrl as any)!,
        name: `${s.name} + ${ngtProduct.productName}`,
        price,
        originalPrice,
        savingsPercentage: Math.floor(((originalPrice - price) / originalPrice) * 100),
      };
    });
  return data;
};
export type InternetBundle = ReturnType<typeof mapSubsriptionsToInternetBundle>[number];

export const toProductBundle = (internetBundle: InternetBundle): InternetProductWithBundleId => {
  const pkg = internetBundle.ngtProduct;
  const product: InternetProductWithBundleId = {
    id: 'internet-product',
    bundleId: 'internet-bundle',
    isPreChosen: false,
    // @ts-expect-error
    displayData: {
      name: pkg.productName,
    },
    productType: ProductType.Internet,
    offer: {
      commercialProductId: internetBundle.sanityProduct.iccProductId,
      compositeId: `${internetBundle.sanityProduct.iccProductId}-0-0-12-${internetBundle.sanityProduct.iccAlacarteId}`,
      contractPeriod: 12,
      offerId: 0,
      listPriceId: 0,
    },
    pricing: {
      isRecurring: true,
      price: pkg.productPrice,
      originalPrice: pkg.originalProductPrice,
      offerPeriodInMonths: 12,
      offerEndDate: addMonths(new Date(), 12).toISOString(),
    },
  };
  return product;
};

export const getInstallationAddress = (availabilityResponse: NgtAvailabilityResponseYes) => {
  const addressList = availabilityResponse.availabilityInfo.addresses;
  if (addressList.length === 1) {
    return addressList[0];
  }
  // There might be multiple addresses, if that happens we've been told by NGT to use the first one that has "apartment" set. Fallback to first in list.
  return addressList.find((a) => !!a.address.apartment) ?? addressList[0];
};
